<template>
  <div id="giftcard">
    <slot name="banner">
      <div class="max-w-screen-xl m-auto">
        <Banner
          hide-button
          :featured-media="[featuredMedia]"
          :bg-color="bannerData.bgColor"
          :color="bannerData.titleColor"
          :description="bannerData.description"
          :text="bannerData.title"
          test-id="giftcard-title"
          show-giftbox
        />
      </div>
    </slot>

    <div class="flex flex-col gap-5 text-sm md:text-base">
      <div class="mt-10 mb-6 md:mt-20">
        <h2 class="mb-2">
          {{ $t('giftcard.bookingSolution.gridTitle') }}
        </h2>
        <h5 class="mb-2">
          {{ $t('giftcard.bookingSolution.gridSubtitle') }}
        </h5>
        <div class="w-full flex flex-col md:flex-row gap-4 mt-10">
          <div
            v-for="(item, index) in getGridsContent"
            :key="'grid-item-' + index"
            class="flex-1 px-4 py-10 rounded-xl text-center bg-[#f9f7f5]"
          >
            <h4>{{ item?.title }}</h4>
            <div class="prose mt-1.5 leading-6" v-html="item.content" />
          </div>
        </div>
      </div>
      <div v-if="faq?.sections?.length" class="flex-auto w-auto">
        <Faq :content="faq?.sections" :is-i18n-object="faq" />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

const { t: $t, tm: $tm, rt: $rt } = useI18n()
const {
  public: { baseUrl },
} = useRuntimeConfig()

const props = withDefaults(
  defineProps<{
    bannerPageKey?: string
    iconHeadingList?: IconHeadingItem[][]
    giftcardIconsList?: { icon: Component; label: string }[]
    faq?: {
      sections: {
        title: string
        questions: {
          question: string
          answer: string
        }[]
      }[]
    }
    grids?: {
      title?: string
      content?: string
    }[]
    banner?: string
  }>(),
  {
    displayBannerGiftcardBox: true,
    displayGiftcardEventsShowcase: true,
    displayGiftcardDetailsSection: true,
    displayEventShowcase: true,
    iconHeadingList: undefined,
    giftcardIconsList: undefined,
    faq: undefined,
    bannerPageKey: undefined,
    grids: () => [],
    banner: undefined,
  }
)

const gridContentDefaults = computed(() => {
  return [
    {
      index: 0,
      title: $t('giftcard.bookingSolution.grid1.title'),
      content: $t('giftcard.bookingSolution.grid1.content'),
    },
    {
      index: 1,
      title: $t('giftcard.bookingSolution.grid2.title'),
      content: $t('giftcard.bookingSolution.grid2.content'),
    },
    {
      index: 2,
      title: $t('giftcard.bookingSolution.grid3.title'),
      content: $t('giftcard.bookingSolution.grid3.content'),
    },
  ]
})

const getGridsContent = computed(() => {
  const items = props.grids
  if (!items) return gridContentDefaults.value

  const result = []
  const defaults = gridContentDefaults.value

  for (let i = 0; i < defaults.length; i++) {
    const found = items.find((item) => parseInt(item.index) === i)
    result.push(found || defaults[i])
  }

  return result
})

// const bannerDefaults = $tm('giftcard.header') ? $rt($tm('giftcard.header')) : {}
const bannerDefaults = Object.keys($tm('giftcard.header')).reduce(
  (carry, item) => ({
    ...carry,
    [item]: $rt($tm('giftcard.header')[item]),
  }),
  {}
)
const { bannerData } = await useBannerFromStrapi(
  props.bannerPageKey ?? 'giftcard',
  {
    ...bannerDefaults,
    bgColor: 'transparent',
  }
)
const featuredMedia = computed(() => {
  const banner = bannerData?.value
  if (!banner) return

  return {
    id: banner?.bgImage?.id || 'banner',
    src: props.banner || banner?.bgImage?.url,
    alt: banner?.bgImage?.alternativeText || '',
  }
})

useSeoMeta({
  'og:image': baseUrl + $t('giftcard.header.image'),
})
</script>
